import { Seo } from '../components/academy/Seo';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import ToolkitPromo from '../components/ToolkitPromo';
import {
  Gauge, FileText, ThumbsUp, CalendarClock, Camera,
  ArrowLeft, ShieldCheck, Check, NotebookPen,
  Car, ScanLine, Wrench, Activity
} from 'lucide-react';

const sectionScores = [
  { label: 'Exterior', value: 95 },
  { label: 'Interior', value: 92 },
  { label: 'Mechanical', value: 96 },
  { label: 'Tires', value: 93 },
];

const ownership = [
  { period: '0–6 months', total: '$1,200–$1,600', items: ['Tires $1,100–$1,300', 'Fluids & Service $100–$150', 'Rock chip touch-up $75–$100'] },
  { period: '6–12 months', total: '$200–$600', items: ['Front brakes $400–$500', 'Cabin filter $60–$80', 'Registration $50–$80'] },
  { period: '12–24 months', total: '$300–$700', items: ['Coolant service $150–$250', 'Rear brakes $300–$400', 'Battery $200–$300'] },
];

const photos = [
  { src: 'https://static.prod-images.emergentagent.com/jobs/5a750074-0ac9-4c5b-a0c0-950bd565c9c5/images/337d38dabb8675628b862150a8e43b0d12fb2cd5a29fc544b0d587302c87b087.png', caption: 'Front quarter — driver side' },
  { src: 'https://static.prod-images.emergentagent.com/jobs/5a750074-0ac9-4c5b-a0c0-950bd565c9c5/images/071942797160df2189fd7ab2253efa98dd754dbb1ad835f7f87259934c6bf832.png', caption: 'Cabin — dashboard & infotainment' },
  { src: 'https://static.prod-images.emergentagent.com/jobs/5a750074-0ac9-4c5b-a0c0-950bd565c9c5/images/fff5943a4765ac24b5b35110f1f6251879a2424b2d8218faf3a178b6c8114424.png', caption: 'Engine bay — 5.3L V8' },
  { src: 'https://static.prod-images.emergentagent.com/jobs/5a750074-0ac9-4c5b-a0c0-950bd565c9c5/images/9ca2f28e65f50da328f33759b9096fe5a86cd2a4baf096bd6d1a6c094629f93f.png', caption: 'Rear — LED tail lights' },
];

const reportIncludes = [
  { icon: FileText, label: 'Executive Summary', desc: 'A plain-English verdict on the vehicle.', color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'hover:border-sky-500/50' },
  { icon: Car, label: 'Vehicle Snapshot', desc: 'Year, make, model, mileage, VIN & color.', color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'hover:border-orange-500/50' },
  { icon: CalendarClock, label: 'Upcoming Ownership', desc: 'What you\u2019ll likely spend over 24 months.', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'hover:border-emerald-500/50' },
  { icon: ScanLine, label: 'Exterior / Interior Conditions', desc: 'Body, paint, cabin & feature condition.', color: 'text-violet-400', bg: 'bg-violet-500/10', border: 'hover:border-violet-500/50' },
  { icon: Wrench, label: 'Mechanical Health', desc: 'Engine, transmission, brakes & suspension.', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'hover:border-amber-500/50' },
  { icon: Gauge, label: 'Tread Depth', desc: 'Measured tire wear on all four corners.', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'hover:border-cyan-500/50' },
  { icon: Activity, label: 'OBD Diagnostics', desc: 'Live scan for stored & pending fault codes.', color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'hover:border-rose-500/50' },
  { icon: ThumbsUp, label: 'Final Recommendation', desc: 'Your overall CarLens Confidence Score™.', color: 'text-teal-400', bg: 'bg-teal-500/10', border: 'hover:border-teal-500/50' },
];

const execSummaryImg = 'https://customer-assets.emergentagent.com/job_5a750074-0ac9-4c5b-a0c0-950bd565c9c5/artifacts/sdaxej2l_47BB2B63-4434-420A-B52E-947A20C95406.png';
const certificateImg = 'https://customer-assets.emergentagent.com/job_5a750074-0ac9-4c5b-a0c0-950bd565c9c5/artifacts/qn1g4y4l_0615A2EE-63C2-4F1C-9BFE-E3C45D8628FB.PNG';

const CheckVehicleButton = ({ testid, navigate, variant = 'solid' }) => (
  <Button
    size="lg"
    variant={variant === 'outline' ? 'outline' : undefined}
    className={variant === 'outline'
      ? 'border-white/20 text-white hover:bg-white/5 px-8 py-6 text-lg rounded-full'
      : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-full shadow-lg shadow-orange-500/20'}
    onClick={() => navigate('/book')}
    data-testid={testid}
  >
    Book Inspection Now
  </Button>
);

const ConfidenceGauge = ({ score = 94 }) => {
  const r = 84;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <div className="relative w-56 h-56">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
        <circle cx="100" cy="100" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="12" />
        <circle
          cx="100" cy="100" r={r} fill="none" stroke="url(#grad)" strokeWidth="12"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
        />
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22c55e" />
            <stop offset="100%" stopColor="#4ade80" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-6xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{score}</span>
        <span className="text-zinc-400 text-sm">out of 100</span>
        <span className="mt-1 text-[10px] font-semibold tracking-normal uppercase text-green-400 text-center leading-tight px-4">Excellent Condition</span>
      </div>
    </div>
  );
};

const WhatYouReceive = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen text-white" style={{ background: 'linear-gradient(180deg, #141416 0%, #1c1c1f 55%, #100f11 100%)' }} data-testid="what-you-receive-page">
      <Seo siteName="CarLens" title="The CarLens Vehicle Decision Report" description="See exactly what you receive after a CarLens inspection: Confidence Score, negotiation power, ownership forecast, photo evidence, and a clear recommendation." path="/what-you-receive" />
      {/* Nav */}
      <header className="border-b border-white/5 backdrop-blur-md bg-black/20 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto pl-6 pr-20 sm:pr-32 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-zinc-300 hover:text-white transition-colors" data-testid="wyr-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <CheckVehicleButton testid="wyr-nav-book-btn" navigate={navigate} />
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-orange-500/15 blur-[140px] rounded-full pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 pt-24 pb-16 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 mb-8">
            <ShieldCheck className="w-4 h-4 text-orange-400" />
            <span className="text-xs tracking-widest uppercase text-zinc-200">The CarLens Vehicle Decision Report™</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight leading-none" style={{ fontFamily: 'Space Grotesk' }}>
            See What You'll <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">Receive</span>
          </h1>
          <p className="text-zinc-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            A clear, unbiased verdict on any vehicle — so you know exactly what you're buying before you commit.
          </p>

          {/* The 3 immediate things */}
          <div className="grid sm:grid-cols-3 gap-4 mt-14 text-left">
            <div className="rounded-2xl border border-green-500/40 bg-green-500/10 backdrop-blur p-6" data-testid="hero-confidence">
              <Gauge className="w-6 h-6 text-green-400 mb-3" />
              <p className="text-sm text-zinc-400 mb-1">Confidence Score™</p>
              <p className="text-4xl font-bold text-green-400" style={{ fontFamily: 'Space Grotesk' }}>94<span className="text-lg text-zinc-500">/100</span></p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6" data-testid="hero-summary">
              <FileText className="w-6 h-6 text-zinc-300 mb-3" />
              <p className="text-sm text-zinc-400 mb-1">Executive Summary</p>
              <p className="text-base text-zinc-200 leading-snug">A plain-English verdict on condition, risks & value.</p>
            </div>
            <div className="rounded-2xl border border-orange-500/40 bg-orange-500/10 backdrop-blur p-6" data-testid="hero-recommendation">
              <ThumbsUp className="w-6 h-6 text-orange-400 mb-3" />
              <p className="text-sm text-zinc-400 mb-1">Recommendation</p>
              <p className="text-2xl font-bold text-orange-300" style={{ fontFamily: 'Space Grotesk' }}>Buy with Confidence</p>
            </div>
          </div>

          <div className="mt-12">
            <CheckVehicleButton testid="wyr-hero-book-btn" navigate={navigate} />
            <p className="text-zinc-500 text-sm mt-3">Get a CarLens Vehicle Decision Report™ for the car you're considering.</p>
          </div>
        </div>
      </section>

      {/* What Your Report Includes */}
      <Section eyebrow="What's Inside" title="Everything your report includes">
        <p className="text-zinc-400 mb-10 max-w-2xl">Each CarLens Vehicle Decision Report™ walks through every part of the vehicle — here's what you'll find inside.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="wyr-report-includes">
          {reportIncludes.map((item, i) => (
            <div key={item.label} className={`rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors ${item.border}`} data-testid={`wyr-include-${i}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className={`font-bold text-lg tabular-nums ${item.color}`} style={{ fontFamily: 'Space Grotesk' }}>{String(i + 1).padStart(2, '0')}</span>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${item.bg}`}>
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                </div>
              </div>
              <h3 className="text-white font-semibold mb-1">{item.label}</h3>
              <p className="text-sm text-zinc-400 leading-snug">{item.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Confidence Score */}
      <Section eyebrow="01 — Confidence Score™" title="One number that tells the whole story">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="flex justify-center">
            <ConfidenceGauge score={94} />
          </div>
          <div>
            <p className="text-zinc-300 leading-relaxed mb-6">
              Every CarLens inspection distills 200+ checkpoints into a single Confidence Score™ — averaged from weighted sections so you can judge a vehicle at a glance.
            </p>
            <div className="space-y-3">
              {sectionScores.map((s) => (
                <div key={s.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-300">{s.label}</span>
                    <span className="text-white font-semibold">{s.value}/100</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-green-500 to-green-400" style={{ width: `${s.value}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Executive Summary */}
      <Section eyebrow="02 — Executive Summary" title="The verdict, in plain English">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] p-8 md:p-12">
          <FileText className="w-8 h-8 text-orange-400 mb-6" />
          <p className="text-xl md:text-2xl text-zinc-100 leading-relaxed font-light">
            "A meticulously maintained 2025 Chevrolet Silverado 1500 LTZ Crew Cab (4WD) with the proven 5.3L V8 and 10-speed automatic. Clean OBDII diagnostics with no fault codes, strong tire tread, and excellent mechanical health across the board. No accident indicators or hidden issues detected — an outstanding, well-maintained truck that's ready to work or play."
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {['No fault codes', 'Strong V8 performance', 'Excellent tread depth', 'Clean undercarriage'].map((t) => (
              <span key={t} className="inline-flex items-center gap-2 text-sm text-zinc-300 bg-white/5 border border-white/10 rounded-full px-4 py-1.5">
                <Check className="w-4 h-4 text-green-400" /> {t}
              </span>
            ))}
          </div>
        </div>
      </Section>

      {/* Sample Executive Summary Visual */}
      <Section eyebrow="A Real Sample" title="Your one-page verdict, at a glance">
        <p className="text-zinc-400 mb-10 max-w-2xl">This is exactly the kind of Vehicle Executive Summary you'll receive — Confidence Score, section grades, what we love, and what to watch, all on a single page.</p>
        <div className="flex justify-center">
          <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 max-w-2xl w-full" data-testid="wyr-exec-summary-image">
            <img src={execSummaryImg} alt="CarLens Vehicle Executive Summary sample" className="w-full h-auto block" />
          </div>
        </div>
      </Section>

      {/* Ownership Cost Forecast */}
      <Section eyebrow="03 — Ownership Cost Forecast" title="Your next 24 months, mapped out">
        <p className="text-zinc-400 mb-8 max-w-2xl">Projected costs based on real inspection findings — tire wear, brake condition, and service intervals. Estimated 2-year total: <span className="text-white font-semibold">$1,700–$2,900</span>.</p>
        <div className="grid md:grid-cols-3 gap-6">
          {ownership.map((o) => (
            <div key={o.period} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="flex items-center gap-2 mb-4">
                <CalendarClock className="w-5 h-5 text-zinc-300" />
                <span className="text-sm text-zinc-400">{o.period}</span>
              </div>
              <p className="text-3xl font-bold text-white mb-5" style={{ fontFamily: 'Space Grotesk' }}>{o.total}</p>
              <ul className="space-y-2">
                {o.items.map((it) => (
                  <li key={it} className="text-sm text-zinc-300 flex items-start gap-2">
                    <span className="text-zinc-300 mt-0.5">•</span> {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* Photo Evidence */}
      <Section eyebrow="04 — Photo Evidence" title="Every claim, backed by a photo">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {photos.map((p, i) => (
            <div key={i} className="group relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/3]">
              <img src={p.src} alt={p.caption} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 flex items-center gap-2">
                <Camera className="w-4 h-4 text-orange-400" />
                <span className="text-xs text-white">{p.caption}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* CarLens Advisor Notes */}
      <Section eyebrow="05 — CarLens Advisor Notes" title="A trusted advisor's final word">
        <div className="rounded-3xl border border-orange-500/30 bg-gradient-to-br from-orange-500/[0.08] to-transparent p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-orange-500/15 flex items-center justify-center">
              <NotebookPen className="w-5 h-5 text-orange-400" />
            </div>
            <span className="text-orange-300 font-semibold tracking-wide">CarLens Advisor</span>
          </div>
          <p className="text-lg md:text-2xl text-zinc-100 leading-relaxed font-light">
            "This 2025 Chevrolet Silverado 1500 LTZ is an exceptionally well-kept truck with the dependable 5.3L V8 and smooth 10-speed transmission. It presents in outstanding condition mechanically and cosmetically. The only item to monitor is the front brake pads at 60% (replacement expected within ~8,000 miles). No accident history, clean diagnostics, and a well-maintained interior make this a confident buy for anyone seeking a reliable, capable pickup."
          </p>
        </div>
      </Section>

      {/* Certificate of Inspection */}
      <Section eyebrow="06 — Certificate of Inspection" title="An official, verifiable certificate">
        <p className="text-zinc-400 mb-10 max-w-2xl">Every completed inspection includes an official CarLens Certificate of Inspection — a record of your 200+ point independent inspection you can share with sellers, buyers, or lenders.</p>
        <div className="flex justify-center">
          <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 max-w-md w-full" data-testid="wyr-certificate-image">
            <img src={certificateImg} alt="CarLens Certificate of Inspection" className="w-full h-auto block" />
          </div>
        </div>
      </Section>

      {/* Toolkit promotion */}
      <div className="max-w-5xl mx-auto px-6 pt-8">
        <ToolkitPromo variant="compact" />
      </div>

      {/* Final CTA */}
      <section className="max-w-5xl mx-auto px-6 py-24">
        <div className="relative rounded-3xl border-2 border-orange-500/60 bg-gradient-to-br from-orange-500/10 to-transparent p-10 md:p-16 text-center overflow-hidden">
          <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-orange-500/15 blur-3xl rounded-full" />
          <div className="relative">
            <h2 className="text-3xl md:text-5xl font-bold mb-5" style={{ fontFamily: 'Space Grotesk' }}>
              Ready to know before you buy?
            </h2>
            <p className="text-zinc-300 text-lg max-w-xl mx-auto mb-10">
              Get a complete CarLens Vehicle Decision Report™ for the car you're considering — and buy with total confidence.
            </p>
            <div className="flex justify-center">
              <CheckVehicleButton testid="wyr-book-btn" navigate={navigate} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const Section = ({ eyebrow, title, children }) => (
  <section className="max-w-7xl mx-auto px-6 py-20 border-t border-white/5">
    <p className="text-orange-400 text-sm font-semibold tracking-widest uppercase mb-3">{eyebrow}</p>
    <h2 className="text-3xl md:text-4xl font-bold mb-12 max-w-2xl" style={{ fontFamily: 'Space Grotesk' }}>{title}</h2>
    {children}
  </section>
);

export default WhatYouReceive;
